import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, Edit2, Save, Download, Upload, Home, Building, Layers, Box, DoorOpen, Euro, Calculator, FileText, Check, Database, X, BarChart3, AlertTriangle, Copy, Search, Filter, Wifi, WifiOff, FolderOpen, FilePlus, PenTool } from 'lucide-react';
import { loadAllData, saveAllData, deleteMaterialApi, deleteComponentTypeApi, checkApiAvailable, loadProjects, loadProject, deleteProjectApi } from './api';
import { StoreyEditor3D } from './components/storey-editor';

// ===== HELPER FUNCTIONS =====
const generateId = () => Math.random().toString(36).substr(2, 9);
const formatNumber = (n, d = 2) => n != null ? n.toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d }) : '-';
const getCatIcon = (cat) => ({ roof: '🏠', wall: '🧱', floor: '⬛', ceiling: '📦' }[cat] || '📦');
const getCatColor = (cat) => ({ roof: 'bg-orange-100 text-orange-800', wall: 'bg-blue-100 text-blue-800', floor: 'bg-amber-100 text-amber-800', ceiling: 'bg-purple-100 text-purple-800' }[cat] || 'bg-gray-100');

// Ähnlichkeits-Check für Duplikate
const isSimilar = (str1, str2) => {
  const s1 = str1.toLowerCase().replace(/[^a-zäöü0-9]/g, '');
  const s2 = str2.toLowerCase().replace(/[^a-zäöü0-9]/g, '');
  return s1 === s2 || s1.includes(s2) || s2.includes(s1);
};

// ===== INITIAL DATA =====
const defaultMaterials = {
  "MAT_GIPSKARTONPLATTE": { id: "MAT_GIPSKARTONPLATTE", name: "Gipskartonplatte", category: "Board", lambda: 0.250, mu: 4, density: 680, price: 8, priceUnit: "m²" },
  "MAT_GIPSPUTZ": { id: "MAT_GIPSPUTZ", name: "Gipsputz", category: "Coating", lambda: 0.35, mu: 10, density: 1200, price: 8, priceUnit: "m²" },
  "MAT_PAVAFLEX": { id: "MAT_PAVAFLEX", name: "PAVATEX PAVAFLEX CONFORT", category: "Insulation", lambda: 0.040, mu: 2, density: 55, price: 18, priceUnit: "m²" },
  "MAT_PAVATHERM": { id: "MAT_PAVATHERM", name: "PAVATHERM-Plus", category: "Insulation", lambda: 0.045, mu: 5, density: 190, price: 28, priceUnit: "m²" },
  "MAT_PAVATEX_LDB": { id: "MAT_PAVATEX_LDB", name: "PAVATEX LDB 0.02", category: "Membrane", lambda: 0.220, sd: 0.02, density: 300, price: 4, priceUnit: "m²" },
  "MAT_FICHTE": { id: "MAT_FICHTE", name: "Fichte Konstruktionsholz", category: "Timber", lambda: 0.130, mu: 40, density: 450, price: 650, priceUnit: "m³" },
  "MAT_HOLZFASER": { id: "MAT_HOLZFASER", name: "Holzfaserdämmplatte", category: "Insulation", lambda: 0.042, mu: 5, density: 160, price: 28, priceUnit: "m²" },
  "MAT_VOLLZIEGEL": { id: "MAT_VOLLZIEGEL", name: "Vollziegel", category: "Masonry", lambda: 0.96, mu: 8, density: 1800, price: 95, priceUnit: "m³" },
  "MAT_KALKSANDSTEIN": { id: "MAT_KALKSANDSTEIN", name: "Kalksandstein KS 12", category: "Masonry", lambda: 0.56, mu: 15, density: 1400, price: 85, priceUnit: "m³" },
  "MAT_EPS": { id: "MAT_EPS", name: "EPS 035", category: "Insulation", lambda: 0.035, mu: 60, density: 30, price: 45, priceUnit: "m³" },
  "MAT_KALKZEMENTPUTZ": { id: "MAT_KALKZEMENTPUTZ", name: "Kalkzementputz", category: "Coating", lambda: 1.0, mu: 25, density: 1800, price: 14, priceUnit: "m²" },
  "MAT_ARMIERMOERTEL": { id: "MAT_ARMIERMOERTEL", name: "Klebe-/Armiermörtel", category: "Adhesive", lambda: 0.54, mu: 11, density: 1400, price: 12, priceUnit: "m²" },
  "MAT_SILIKONPUTZ": { id: "MAT_SILIKONPUTZ", name: "Silikonharzputz", category: "Coating", lambda: 0.87, mu: 50, density: 1800, price: 18, priceUnit: "m²" },
  "MAT_FALZZIEGEL": { id: "MAT_FALZZIEGEL", name: "Falzziegel inkl. Lattung", category: "Cladding", lambda: null, density: 1800, price: 45, priceUnit: "m²" },
  "MAT_STAHLBETON": { id: "MAT_STAHLBETON", name: "Stahlbeton", category: "Concrete", lambda: 2.3, mu: 100, density: 2500, price: 200, priceUnit: "m³" },
  "MAT_ESTRICH": { id: "MAT_ESTRICH", name: "Zementestrich", category: "Screed", lambda: 1.4, mu: 30, density: 2000, price: 25, priceUnit: "m²" },
  "MAT_TRITTSCHALL": { id: "MAT_TRITTSCHALL", name: "Trittschalldämmung", category: "Insulation", lambda: 0.040, mu: 1, density: 25, price: 6, priceUnit: "m²" },
  "MAT_PARKETT": { id: "MAT_PARKETT", name: "Parkett Eiche", category: "Flooring", lambda: 0.20, mu: 50, density: 700, price: 45, priceUnit: "m²" },
};

const defaultComponentTypes = {
  "DA_PAVATEX_01": {
    id: "DA_PAVATEX_01", name: "Dachsanierung Pavatex/Beton", shortCode: "DA-PAV-01", category: "roof",
    uValue: 0.17, totalThickness: 506, source: "ubakus.de", sourceFile: "Dachsanierung_pavatex_Betonsparren.pdf",
    phaseShift: 18.2, dryingReserve: 21691,
    layers: [
      { name: "Gipskartonplatte", materialRef: "MAT_GIPSKARTONPLATTE", thickness: 12.5 },
      { name: "Installationsebene/Fichte", materialRef: "MAT_FICHTE", thickness: 40 },
      { name: "PAVAFLEX CONFORT", materialRef: "MAT_PAVAFLEX", thickness: 180 },
      { name: "Luftdichtbahn LDB", materialRef: "MAT_PAVATEX_LDB", thickness: 0.72 },
      { name: "PAVATHERM-Plus", materialRef: "MAT_PAVATHERM", thickness: 140 },
      { name: "Falzziegel + Lattung", materialRef: "MAT_FALZZIEGEL", thickness: 0 },
    ]
  },
  "AW_HWF_01": {
    id: "AW_HWF_01", name: "Außenwand HWF-WDVS auf Ziegel", shortCode: "AW-HWF-01", category: "wall",
    uValue: 0.18, totalThickness: 440, source: "ubakus.de",
    layers: [
      { name: "Armiermörtel außen", materialRef: "MAT_ARMIERMOERTEL", thickness: 5 },
      { name: "Holzfaserdämmplatte", materialRef: "MAT_HOLZFASER", thickness: 160 },
      { name: "Armiermörtel", materialRef: "MAT_ARMIERMOERTEL", thickness: 5 },
      { name: "Kalkzementputz", materialRef: "MAT_KALKZEMENTPUTZ", thickness: 20 },
      { name: "Vollziegel", materialRef: "MAT_VOLLZIEGEL", thickness: 240 },
      { name: "Gipsputz innen", materialRef: "MAT_GIPSPUTZ", thickness: 10 },
    ]
  },
  "AW_EPS_01": {
    id: "AW_EPS_01", name: "Außenwand EPS-WDVS auf KS", shortCode: "AW-EPS-01", category: "wall",
    uValue: 0.22, totalThickness: 365, source: "Standard",
    layers: [
      { name: "Silikonharzputz", materialRef: "MAT_SILIKONPUTZ", thickness: 5 },
      { name: "Armiermörtel", materialRef: "MAT_ARMIERMOERTEL", thickness: 5 },
      { name: "EPS-Dämmung", materialRef: "MAT_EPS", thickness: 100 },
      { name: "Kalksandstein", materialRef: "MAT_KALKSANDSTEIN", thickness: 240 },
      { name: "Gipsputz", materialRef: "MAT_GIPSPUTZ", thickness: 15 },
    ]
  },
  "IW_KS_01": {
    id: "IW_KS_01", name: "Innenwand KS tragend", shortCode: "IW-KS-01", category: "wall",
    uValue: null, totalThickness: 205, source: "Standard",
    layers: [
      { name: "Gipsputz", materialRef: "MAT_GIPSPUTZ", thickness: 15 },
      { name: "Kalksandstein", materialRef: "MAT_KALKSANDSTEIN", thickness: 175 },
      { name: "Gipsputz", materialRef: "MAT_GIPSPUTZ", thickness: 15 },
    ]
  },
  "DE_GD_01": {
    id: "DE_GD_01", name: "Geschossdecke Standard", shortCode: "DE-GD-01", category: "ceiling",
    uValue: null, totalThickness: 310, source: "Standard",
    layers: [
      { name: "Parkett", materialRef: "MAT_PARKETT", thickness: 15 },
      { name: "Estrich", materialRef: "MAT_ESTRICH", thickness: 65 },
      { name: "Trittschalldämmung", materialRef: "MAT_TRITTSCHALL", thickness: 30 },
      { name: "Stahlbetondecke", materialRef: "MAT_STAHLBETON", thickness: 200 },
    ]
  },
  "BO_PAR_01": {
    id: "BO_PAR_01", name: "Bodenbelag Parkett Standard", shortCode: "BO-PAR-01", category: "floor",
    uValue: null, totalThickness: 110, source: "Standard",
    layers: [
      { name: "Parkett", materialRef: "MAT_PARKETT", thickness: 15 },
      { name: "Estrich", materialRef: "MAT_ESTRICH", thickness: 65 },
      { name: "Trittschalldämmung", materialRef: "MAT_TRITTSCHALL", thickness: 30 },
    ]
  },
  "BO_FLI_01": {
    id: "BO_FLI_01", name: "Bodenbelag Fliesen", shortCode: "BO-FLI-01", category: "floor",
    uValue: null, totalThickness: 100, source: "Standard",
    layers: [
      { name: "Fliesen inkl. Kleber", materialRef: "MAT_ESTRICH", thickness: 20 },
      { name: "Estrich", materialRef: "MAT_ESTRICH", thickness: 50 },
      { name: "Trittschalldämmung", materialRef: "MAT_TRITTSCHALL", thickness: 30 },
    ]
  },
};

const createEmptyProject = () => ({
  id: generateId(),
  name: "Neues Projekt",
  created: new Date().toISOString(),
  storeys: [{
    id: generateId(),
    name: "Erdgeschoss",
    level: 0,
    height: 2.75,
    spaces: [{
      id: generateId(),
      name: "Raum 1",
      components: []
    }]
  }]
});

// ===== STORAGE (jetzt via API mit localStorage Fallback) =====
// Die Funktionen loadAllData, saveAllData etc. sind in api.js definiert

// ===== MODAL COMPONENT =====
const Modal = ({ isOpen, onClose, title, children, wide }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col ${wide ? 'w-full max-w-4xl' : 'w-full max-w-lg'}`}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-bold text-lg">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
};

// ===== MATERIAL EDITOR =====
const MaterialEditor = ({ material, onSave, onClose, existingMaterials }) => {
  const [mat, setMat] = useState(material || { id: '', name: '', category: 'Insulation', lambda: '', mu: '', density: '', price: '', priceUnit: 'm²' });
  const [warning, setWarning] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);

  const checkDuplicate = (name) => {
    const similar = Object.values(existingMaterials).find(m => m.id !== mat.id && isSimilar(m.name, name));
    if (similar) setWarning(`Ähnliches Material existiert: "${similar.name}"`);
    else setWarning(null);
  };

  const lookupPrice = async () => {
    if (!mat.name) return;
    setLoadingPrice(true);
    try {
      const res = await fetch(`/api/price-lookup/${encodeURIComponent(mat.name)}`);
      if (res.ok) {
        const data = await res.json();
        setPriceInfo(data);
        if (data.estimate?.avg_price && (!mat.price || parseFloat(mat.price) === 0)) {
          setMat(prev => ({ ...prev, price: data.estimate.avg_price, priceUnit: data.estimate.unit || 'm²' }));
        }
      }
    } catch (e) {
      console.error('Price lookup failed:', e);
    }
    setLoadingPrice(false);
  };

  const handleSave = () => {
    const id = mat.id || `MAT_${mat.name.toUpperCase().replace(/[^A-Z0-9]/g, '_').slice(0, 20)}_${generateId().slice(0, 4)}`;
    onSave({ ...mat, id, lambda: parseFloat(mat.lambda) || null, mu: parseFloat(mat.mu) || null, density: parseFloat(mat.density) || null, price: parseFloat(mat.price) || 0 });
  };
  
  return (
    <div className="space-y-4">
      {warning && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded flex items-center gap-2">
          <AlertTriangle size={16} /> {warning}
        </div>
      )}
      <div>
        <label className="block text-sm font-medium mb-1">Name *</label>
        <input value={mat.name} onChange={(e) => { setMat({ ...mat, name: e.target.value }); checkDuplicate(e.target.value); }} className="w-full border rounded px-3 py-2" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Kategorie</label>
          <select value={mat.category} onChange={(e) => setMat({ ...mat, category: e.target.value })} className="w-full border rounded px-3 py-2">
            {['Insulation', 'Masonry', 'Concrete', 'Timber', 'Board', 'Coating', 'Membrane', 'Adhesive', 'Cladding', 'Flooring', 'Screed', 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">λ [W/(mK)]</label>
          <input type="number" step="0.001" value={mat.lambda} onChange={(e) => setMat({ ...mat, lambda: e.target.value })} className="w-full border rounded px-3 py-2" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">μ [-]</label>
          <input type="number" value={mat.mu} onChange={(e) => setMat({ ...mat, mu: e.target.value })} className="w-full border rounded px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ρ [kg/m³]</label>
          <input type="number" value={mat.density} onChange={(e) => setMat({ ...mat, density: e.target.value })} className="w-full border rounded px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Preis</label>
          <div className="flex gap-1">
            <input type="number" step="0.01" value={mat.price} onChange={(e) => setMat({ ...mat, price: e.target.value })} className="flex-1 border rounded px-3 py-2" />
            <select value={mat.priceUnit} onChange={(e) => setMat({ ...mat, priceUnit: e.target.value })} className="border rounded px-2">
              <option value="m²">€/m²</option>
              <option value="m³">€/m³</option>
            </select>
            <button onClick={lookupPrice} disabled={!mat.name || loadingPrice} className="px-3 py-2 border rounded hover:bg-gray-50 disabled:opacity-50" title="Preis recherchieren">
              <Search size={16} className={loadingPrice ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>
      {priceInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
          <div className="font-medium mb-2">Preis-Recherche: {priceInfo.material}</div>
          {priceInfo.estimate?.avg_price ? (
            <div className="mb-2">
              <span className="text-green-700">Richtwert: {priceInfo.estimate.min_price?.toFixed(2)} - {priceInfo.estimate.max_price?.toFixed(2)} €/{priceInfo.estimate.unit}</span>
              <span className="text-gray-500 ml-2">({priceInfo.estimate.source})</span>
            </div>
          ) : (
            <div className="text-yellow-700 mb-2">Kein Richtwert verfügbar</div>
          )}
          <div className="flex flex-wrap gap-2">
            <span className="text-gray-500">Recherche:</span>
            {Object.entries(priceInfo.searchLinks || {}).map(([name, url]) => (
              <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline capitalize">{name}</a>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <button onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-50">Abbrechen</button>
        <button onClick={handleSave} disabled={!mat.name} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">Speichern</button>
      </div>
    </div>
  );
};

// ===== COMPONENT TYPE EDITOR =====
const ComponentTypeEditor = ({ componentType, onSave, onClose, materials }) => {
  const [ct, setCt] = useState(() => {
    const base = { id: '', name: '', shortCode: '', category: 'wall', uValue: '', totalThickness: 0, source: 'Manuell', layers: [] };
    if (componentType) {
      return { ...base, ...componentType, layers: componentType.layers || [] };
    }
    return base;
  });

  const addLayer = () => setCt({ ...ct, layers: [...(ct.layers || []), { name: '', materialRef: '', thickness: 0 }] });
  const updateLayer = (idx, field, value) => {
    const layers = [...(ct.layers || [])];
    layers[idx] = { ...layers[idx], [field]: field === 'thickness' ? parseFloat(value) || 0 : value };
    const totalThickness = layers.reduce((sum, l) => sum + (l.thickness || 0), 0);
    setCt({ ...ct, layers, totalThickness });
  };
  const deleteLayer = (idx) => {
    const layers = (ct.layers || []).filter((_, i) => i !== idx);
    const totalThickness = layers.reduce((sum, l) => sum + (l.thickness || 0), 0);
    setCt({ ...ct, layers, totalThickness });
  };
  const moveLayer = (idx, dir) => {
    const layers = [...(ct.layers || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= layers.length) return;
    [layers[idx], layers[newIdx]] = [layers[newIdx], layers[idx]];
    setCt({ ...ct, layers });
  };
  
  const handleSave = () => {
    const id = ct.id || `${ct.category.toUpperCase().slice(0, 2)}_${generateId().slice(0, 6).toUpperCase()}`;
    const shortCode = ct.shortCode || `${ct.category.toUpperCase().slice(0, 2)}-${generateId().slice(0, 3).toUpperCase()}`;
    onSave({ ...ct, id, shortCode, uValue: parseFloat(ct.uValue) || null });
  };
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Name *</label>
          <input value={ct.name} onChange={(e) => setCt({ ...ct, name: e.target.value })} className="w-full border rounded px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Kurzcode</label>
          <input value={ct.shortCode} onChange={(e) => setCt({ ...ct, shortCode: e.target.value })} placeholder="z.B. AW-HWF-01" className="w-full border rounded px-3 py-2" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Kategorie</label>
          <select value={ct.category} onChange={(e) => setCt({ ...ct, category: e.target.value })} className="w-full border rounded px-3 py-2">
            <option value="wall">🧱 Wand</option>
            <option value="roof">🏠 Dach</option>
            <option value="floor">⬛ Boden</option>
            <option value="ceiling">📦 Decke</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">U-Wert [W/(m²K)]</label>
          <input type="number" step="0.01" value={ct.uValue} onChange={(e) => setCt({ ...ct, uValue: e.target.value })} className="w-full border rounded px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Gesamtdicke</label>
          <input value={`${ct.totalThickness} mm`} readOnly className="w-full border rounded px-3 py-2 bg-gray-50" />
        </div>
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium">Schichten (von innen nach außen)</label>
          <button onClick={addLayer} className="text-sm text-blue-600 hover:underline flex items-center gap-1"><Plus size={14} /> Schicht</button>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {(ct.layers || []).map((layer, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <span className="w-6 text-center text-xs text-gray-500">{idx + 1}</span>
              <input value={layer.name} onChange={(e) => updateLayer(idx, 'name', e.target.value)} placeholder="Bezeichnung" className="flex-1 border rounded px-2 py-1 text-sm" />
              <select value={layer.materialRef} onChange={(e) => updateLayer(idx, 'materialRef', e.target.value)} className="w-40 border rounded px-2 py-1 text-sm">
                <option value="">-- Material --</option>
                {Object.values(materials).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <input type="number" value={layer.thickness} onChange={(e) => updateLayer(idx, 'thickness', e.target.value)} className="w-20 border rounded px-2 py-1 text-sm text-right" />
              <span className="text-xs text-gray-500">mm</span>
              <button onClick={() => moveLayer(idx, -1)} disabled={idx === 0} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30">↑</button>
              <button onClick={() => moveLayer(idx, 1)} disabled={idx === (ct.layers || []).length - 1} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30">↓</button>
              <button onClick={() => deleteLayer(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><X size={14} /></button>
            </div>
          ))}
          {(!ct.layers || ct.layers.length === 0) && <div className="text-center text-gray-400 py-4">Keine Schichten - klicke "+ Schicht"</div>}
        </div>
      </div>
      
      <div className="flex justify-end gap-2 pt-4 border-t">
        <button onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-50">Abbrechen</button>
        <button onClick={handleSave} disabled={!ct.name || !ct.layers || ct.layers.length === 0} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">Speichern</button>
      </div>
    </div>
  );
};

// ===== PDF IMPORT PREVIEW =====
const PDFImportPreview = ({ onImport, onClose, materials, onMaterialsAdded }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [newMaterials, setNewMaterials] = useState([]);
  const [localMaterials, setLocalMaterials] = useState(materials); // Lokale Kopie für Updates

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Bitte eine PDF-Datei auswählen');
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload-pdf', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success && result.componentType) {
        // Neue Materialien zum lokalen State und Parent hinzufügen
        if (result.newMaterials && Object.keys(result.newMaterials).length > 0) {
          setLocalMaterials(prev => ({ ...prev, ...result.newMaterials }));
          if (onMaterialsAdded) {
            onMaterialsAdded(result.newMaterials);
          }
        }

        // Bereite Daten für Vorschau vor
        setData({
          ...result.componentType,
          layers: (result.componentType.layers || []).map(l => ({
            ...l,
            suggestedMaterial: l.materialRef || ''
          }))
        });
        setNewMaterials(result.materialsAdded || []);
      } else {
        setError(result.message || 'Fehler beim Parsen der PDF');
      }
    } catch (e) {
      setError(`Upload fehlgeschlagen: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const updateLayer = (idx, field, value) => {
    const layers = [...data.layers];
    layers[idx] = { ...layers[idx], [field]: value };
    setData({ ...data, layers });
  };

  const handleImport = () => {
    setImporting(true);
    const componentType = {
      id: data.id || `${(data.category || 'BT').toUpperCase().slice(0, 2)}_${generateId().slice(0, 6).toUpperCase()}`,
      name: data.name,
      shortCode: data.shortCode,
      category: data.category,
      uValue: data.uValue,
      totalThickness: data.totalThickness,
      source: data.source || 'PDF Import',
      layers: (data.layers || []).map(l => ({ name: l.name, materialRef: l.suggestedMaterial || l.materialRef, thickness: l.thickness }))
    };
    onImport(componentType);
  };

  // Phase 1: Datei auswählen
  if (!data) {
    return (
      <div className="space-y-4">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <div className="mb-4">
            <label className="cursor-pointer">
              <span className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                PDF auswählen
              </span>
              <input type="file" accept=".pdf" onChange={handleFileChange} className="hidden" />
            </label>
          </div>
          {file && (
            <div className="text-sm text-gray-600">
              Ausgewählt: <span className="font-medium">{file.name}</span>
            </div>
          )}
          <div className="text-xs text-gray-500 mt-2">
            Unterstützt: ubakus.de PDF-Exporte
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-50">Abbrechen</button>
          <button onClick={handleUpload} disabled={!file || uploading} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {uploading ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Upload size={16} />}
            {uploading ? 'Analysiere...' : 'PDF analysieren'}
          </button>
        </div>
      </div>
    );
  }

  // Phase 2: Vorschau und Import
  return (
    <div className="space-y-4">
      <div className="bg-green-50 border border-green-200 p-3 rounded">
        <div className="flex items-center gap-2 text-green-800 font-medium">
          <Check size={16} /> PDF erfolgreich analysiert
        </div>
        {newMaterials.length > 0 && (
          <div className="text-sm text-green-700 mt-1">
            {newMaterials.length} neue Materialien erkannt
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Name</label>
          <input value={data.name || ''} onChange={(e) => setData({ ...data, name: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Kurzcode</label>
          <input value={data.shortCode || ''} onChange={(e) => setData({ ...data, shortCode: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 text-sm">
        <div className="bg-red-50 p-2 rounded"><div className="text-gray-500">U-Wert</div><div className="font-bold">{data.uValue || '-'} W/(m²K)</div></div>
        <div className="bg-blue-50 p-2 rounded"><div className="text-gray-500">Dicke</div><div className="font-bold">{data.totalThickness || '-'} mm</div></div>
        <div className="bg-green-50 p-2 rounded"><div className="text-gray-500">Schichten</div><div className="font-bold">{data.layers?.length || 0}</div></div>
      </div>

      {data.layers && data.layers.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2">Schichten - Material zuordnen</label>
          <div className="border rounded overflow-hidden">
            <div className="grid grid-cols-12 gap-2 p-2 bg-gray-100 text-xs font-medium">
              <div className="col-span-1">#</div>
              <div className="col-span-5">Schicht</div>
              <div className="col-span-1">d [mm]</div>
              <div className="col-span-5">Material zuordnen</div>
            </div>
            {data.layers.map((layer, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 p-2 border-t text-sm items-center">
                <div className="col-span-1 text-gray-500">{idx + 1}</div>
                <div className="col-span-5 truncate" title={layer.name}>{layer.name}</div>
                <div className="col-span-1">{layer.thickness}</div>
                <div className="col-span-5">
                  <select value={layer.suggestedMaterial || ''} onChange={(e) => updateLayer(idx, 'suggestedMaterial', e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
                    <option value="">-- wählen --</option>
                    {Object.values(localMaterials).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t">
        <button onClick={() => setData(null)} className="px-4 py-2 border rounded hover:bg-gray-50">Andere PDF</button>
        <button onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-50">Abbrechen</button>
        <button onClick={handleImport} disabled={importing} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
          {importing ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Check size={16} />}
          In Katalog übernehmen
        </button>
      </div>
    </div>
  );
};

// ===== MAIN APP =====
export default function MengenermittlungApp() {
  // State
  const [materials, setMaterials] = useState(defaultMaterials);
  const [componentTypes, setComponentTypes] = useState(defaultComponentTypes);
  const [project, setProject] = useState(createEmptyProject());
  const [activeTab, setActiveTab] = useState('project');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [apiOnline, setApiOnline] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Modals
  const [modalMaterial, setModalMaterial] = useState(null);
  const [modalComponentType, setModalComponentType] = useState(null);
  const [showPDFImport, setShowPDFImport] = useState(false);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [projectList, setProjectList] = useState({});
  const [showStoreyEditor, setShowStoreyEditor] = useState(false);
  const [editingStoreyId, setEditingStoreyId] = useState(null);

  // Load from API/localStorage on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const online = await checkApiAvailable();
      setApiOnline(online);

      const data = await loadAllData();
      console.log('Daten geladen von:', data.source);

      if (Object.keys(data.materials).length > 0) {
        setMaterials(data.materials);
      }
      if (Object.keys(data.componentTypes).length > 0) {
        setComponentTypes(data.componentTypes);
      }
      if (data.project) {
        setProject(data.project);
      }
      setIsLoading(false);
    };
    loadData();
  }, []);

  // Save to API/localStorage on change (debounced)
  useEffect(() => {
    if (isLoading) return; // Nicht speichern während initialem Laden

    const saveTimer = setTimeout(async () => {
      setIsSaving(true);
      await saveAllData(materials, componentTypes, project);
      setIsSaving(false);
    }, 1000); // 1 Sekunde Debounce

    return () => clearTimeout(saveTimer);
  }, [materials, componentTypes, project, isLoading]);
  
  // Material CRUD
  const handleSaveMaterial = (mat) => {
    setMaterials({ ...materials, [mat.id]: mat });
    setModalMaterial(null);
    // API wird automatisch via useEffect gespeichert
  };
  const handleDeleteMaterial = (id) => {
    const m = { ...materials };
    delete m[id];
    setMaterials(m);
    deleteMaterialApi(id); // Auch aus API löschen
  };

  // ComponentType CRUD
  const handleSaveComponentType = (ct) => {
    setComponentTypes({ ...componentTypes, [ct.id]: ct });
    setModalComponentType(null);
  };
  const handleDeleteComponentType = (id) => {
    const c = { ...componentTypes };
    delete c[id];
    setComponentTypes(c);
    deleteComponentTypeApi(id);
  };

  // PDF Import
  const handlePDFImport = (ct) => {
    setComponentTypes({ ...componentTypes, [ct.id]: ct });
    setShowPDFImport(false);
  };

  // Project Management
  const handleOpenProjectSelector = async () => {
    const projects = await loadProjects();
    setProjectList(projects);
    setShowProjectSelector(true);
  };

  const handleOpenProject = async (projectId) => {
    const projectData = await loadProject(projectId);
    if (projectData) {
      setProject(projectData);
      setSelectedItem(null);
      setSelectedType(null);
      setShowProjectSelector(false);
    }
  };

  const handleNewProject = () => {
    const newProj = createEmptyProject();
    setProject(newProj);
    setSelectedItem(null);
    setSelectedType(null);
    setShowProjectSelector(false);
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Projekt wirklich löschen?')) {
      await deleteProjectApi(projectId);
      const projects = await loadProjects();
      setProjectList(projects);
      // Wenn aktuelles Projekt gelöscht wurde, neues erstellen
      if (project.id === projectId) {
        handleNewProject();
      }
    }
  };

  // Project CRUD
  const addStorey = () => setProject({ ...project, storeys: [...project.storeys, { id: generateId(), name: `${project.storeys.length}. OG`, level: project.storeys.length, height: 2.75, spaces: [] }] });
  const deleteStorey = (id) => setProject({ ...project, storeys: project.storeys.filter(s => s.id !== id) });
  const updateStorey = (id, data) => setProject({ ...project, storeys: project.storeys.map(s => s.id === id ? { ...s, ...data } : s) });
  
  const addSpace = (storeyId) => {
    setProject({ ...project, storeys: project.storeys.map(s => s.id === storeyId ? { ...s, spaces: [...s.spaces, { id: generateId(), name: 'Neuer Raum', components: [] }] } : s) });
  };
  const deleteSpace = (storeyId, spaceId) => {
    setProject({ ...project, storeys: project.storeys.map(s => s.id === storeyId ? { ...s, spaces: s.spaces.filter(sp => sp.id !== spaceId) } : s) });
  };
  const updateSpace = (storeyId, spaceId, data) => {
    setProject({ ...project, storeys: project.storeys.map(s => s.id === storeyId ? { ...s, spaces: s.spaces.map(sp => sp.id === spaceId ? { ...sp, ...data } : sp) } : s) });
  };

  // Storey Editor - Bearbeitet komplettes Geschoss mit allen Räumen
  const openStoreyEditor = (storeyId) => {
    setEditingStoreyId(storeyId);
    setShowStoreyEditor(true);
  };

  const handleStoreyUpdate = (updatedStorey) => {
    if (!editingStoreyId) return;

    // Update das Geschoss im Projekt (Editor bleibt offen)
    setProject(prev => ({
      ...prev,
      storeys: prev.storeys.map(s => s.id === editingStoreyId ? updatedStorey : s)
    }));
    // Editor wird NICHT mehr geschlossen - nur über X-Button/onClose
  };
  
  const addComponent = (storeyId, spaceId, category) => {
    const newComp = { id: generateId(), name: `Neue ${category === 'wall' ? 'Wand' : category === 'roof' ? 'Dachfläche' : category === 'floor' ? 'Boden' : 'Decke'}`, category, typeRef: null, length: 0, height: category === 'wall' ? 2.5 : 0, grossArea: 0, area: 0, openings: [] };
    setProject({ ...project, storeys: project.storeys.map(s => s.id === storeyId ? { ...s, spaces: s.spaces.map(sp => sp.id === spaceId ? { ...sp, components: [...sp.components, newComp] } : sp) } : s) });
    setSelectedItem(newComp);
    setSelectedType('component');
  };
  const deleteComponent = (storeyId, spaceId, compId) => {
    setProject({ ...project, storeys: project.storeys.map(s => s.id === storeyId ? { ...s, spaces: s.spaces.map(sp => sp.id === spaceId ? { ...sp, components: sp.components.filter(c => c.id !== compId) } : sp) } : s) });
    setSelectedItem(null);
  };
  const updateComponent = (storeyId, spaceId, compId, data) => {
    const updated = { ...selectedItem, ...data };
    if ((data.length !== undefined || data.height !== undefined) && updated.length && updated.height) {
      updated.grossArea = updated.length * updated.height;
      const openingsArea = (updated.openings || []).reduce((sum, o) => sum + (o.width || 0) * (o.height || 0), 0);
      updated.area = updated.grossArea - openingsArea;
    }
    setProject({ ...project, storeys: project.storeys.map(s => s.id === storeyId ? { ...s, spaces: s.spaces.map(sp => sp.id === spaceId ? { ...sp, components: sp.components.map(c => c.id === compId ? updated : c) } : sp) } : s) });
    setSelectedItem(updated);
  };
  
  // Find context
  const findContext = () => {
    for (const storey of project.storeys) {
      if (selectedType === 'storey' && storey.id === selectedItem?.id) return { storey };
      for (const space of storey.spaces) {
        if (selectedType === 'space' && space.id === selectedItem?.id) return { storey, space };
        for (const comp of space.components) {
          if (selectedType === 'component' && comp.id === selectedItem?.id) return { storey, space, component: comp };
        }
      }
    }
    return {};
  };
  const ctx = findContext();
  
  // Hilfsfunktionen für Geometrie
  const calcDistance = (p1, p2) => Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
  const calcPolygonArea = (polygon) => {
    if (!polygon || polygon.length < 3) return 0;
    let area = 0;
    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      area += polygon[i].x * polygon[j].y;
      area -= polygon[j].x * polygon[i].y;
    }
    return Math.abs(area / 2);
  };

  // Quantities calculation - unterstützt sowohl alte (components) als auch neue (walls/spaces) Struktur
  const quantities = useMemo(() => {
    const byMaterial = {};
    const byType = {};
    let totalArea = 0, totalCost = 0;

    // Hilfsfunktion: Material-Kosten für eine Fläche berechnen
    const addMaterialCost = (typeRef, area, category) => {
      if (!typeRef || !componentTypes[typeRef]) return 0;
      let typeCost = 0;
      const type = componentTypes[typeRef];

      (type.layers || []).forEach(layer => {
        const mat = materials[layer.materialRef];
        if (!mat) return;
        if (!byMaterial[layer.materialRef]) {
          byMaterial[layer.materialRef] = { name: mat.name, area: 0, volume: 0, cost: 0, unit: mat.priceUnit };
        }
        byMaterial[layer.materialRef].area += area;
        const vol = area * (layer.thickness / 1000);
        byMaterial[layer.materialRef].volume += vol;
        const cost = mat.priceUnit === 'm²' ? area * mat.price : vol * mat.price;
        byMaterial[layer.materialRef].cost += cost;
        typeCost += cost;
      });
      return typeCost;
    };

    project.storeys.forEach(storey => {
      const storeyHeight = (storey.height || 2.75) * 1000; // in mm
      const points = storey.points || [];

      // NEUE STRUKTUR: Wände aus walls[]
      if (storey.walls && storey.walls.length > 0) {
        storey.walls.forEach(wall => {
          const startPoint = points.find(p => p.id === wall.startPointId);
          const endPoint = points.find(p => p.id === wall.endPointId);
          if (!startPoint || !endPoint) return;

          // Wandlänge in mm, dann zu m
          const lengthMm = calcDistance(startPoint, endPoint);
          const lengthM = lengthMm / 1000;

          // Bruttofläche (m²)
          const grossArea = lengthM * (storeyHeight / 1000);

          // Öffnungsfläche abziehen
          const openingsArea = (wall.openings || []).reduce((sum, op) => {
            return sum + ((op.width || 0) / 1000) * ((op.height || 0) / 1000);
          }, 0);

          const netArea = grossArea - openingsArea;
          totalArea += netArea;

          // Nach Typ gruppieren
          const typeKey = wall.typeRef || '_untyped';
          if (!byType[typeKey]) {
            const typeName = wall.typeRef ? componentTypes[wall.typeRef]?.shortCode || componentTypes[wall.typeRef]?.name || wall.typeRef : 'Ohne Typ';
            byType[typeKey] = { name: typeName, category: 'wall', area: 0, cost: 0, count: 0, length: 0 };
          }
          byType[typeKey].area += netArea;
          byType[typeKey].count++;
          byType[typeKey].length = (byType[typeKey].length || 0) + lengthM;

          // Material-Kosten
          const cost = addMaterialCost(wall.typeRef, netArea, 'wall');
          byType[typeKey].cost += cost;
          totalCost += cost;
        });
      }

      // NEUE STRUKTUR: Geschossdecke auf Geschoss-Ebene (nicht pro Raum!)
      // Die Geschossdecke ist die Summe aller Raumflächen im Geschoss
      let storeyTotalArea = 0;

      if (storey.spaces && storey.spaces.length > 0) {
        storey.spaces.forEach(space => {
          // Raumfläche aus pointIds berechnen (falls vorhanden)
          let spaceArea = space.floorArea || 0;
          if (!spaceArea && space.pointIds && space.pointIds.length >= 3) {
            const polygon = space.pointIds.map(pid => points.find(p => p.id === pid)).filter(Boolean);
            spaceArea = calcPolygonArea(polygon) / 1000000; // mm² zu m²
          }

          storeyTotalArea += spaceArea;

          // ALTE STRUKTUR: components[] (Fallback für bestehende Projekte)
          if (space.components && space.components.length > 0) {
            space.components.forEach(comp => {
              const area = comp.area || comp.grossArea || 0;
              totalArea += area;

              const typeKey = comp.typeRef || '_untyped';
              if (!byType[typeKey]) {
                byType[typeKey] = {
                  name: comp.typeRef ? componentTypes[comp.typeRef]?.name || comp.typeRef : 'Ohne Typ',
                  category: comp.category || 'wall',
                  area: 0,
                  cost: 0
                };
              }
              byType[typeKey].area += area;

              const cost = addMaterialCost(comp.typeRef, area, comp.category);
              byType[typeKey].cost += cost;
              totalCost += cost;
            });
          }
        });
      }

      // Geschossdecke: Eine Decke pro Geschoss (nicht pro Raum)
      // Die Fläche ist die Summe aller Raumflächen
      if (storeyTotalArea > 0 && storey.ceilingTypeRef) {
        const typeKey = storey.ceilingTypeRef;
        if (!byType[typeKey]) {
          const typeName = componentTypes[typeKey]?.shortCode || componentTypes[typeKey]?.name || typeKey;
          byType[typeKey] = { name: typeName, category: 'ceiling', area: 0, cost: 0, count: 0 };
        }
        byType[typeKey].area += storeyTotalArea;
        byType[typeKey].count++;
        totalArea += storeyTotalArea;

        const cost = addMaterialCost(storey.ceilingTypeRef, storeyTotalArea, 'ceiling');
        byType[typeKey].cost += cost;
        totalCost += cost;
      }
    });

    return { byMaterial, byType, totalArea, totalCost };
  }, [project, componentTypes, materials]);
  
  // Export
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify({ materials, componentTypes, project }, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${project.name.replace(/\s+/g, '_')}.json`; a.click();
  };
  
  return (
    <div className="h-screen flex flex-col bg-gray-100 text-sm">
      {/* Header */}
      <header className="bg-white shadow-sm px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calculator className="text-blue-600" size={24} />
          <input value={project.name} onChange={(e) => setProject({ ...project, name: e.target.value })} className="text-lg font-bold border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1" />
        </div>
        <div className="flex items-center gap-2">
          {apiOnline !== null && (
            <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${apiOnline ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {apiOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
              {apiOnline ? 'Online' : 'Offline'}
            </span>
          )}
          <button onClick={handleOpenProjectSelector} className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-gray-50" title="Projekt öffnen"><FolderOpen size={14} /> Öffnen</button>
          <button onClick={handleNewProject} className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-gray-50" title="Neues Projekt"><FilePlus size={14} /> Neu</button>
          <button onClick={exportJSON} className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-gray-50"><Download size={14} /> Export</button>
          <button className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded ${isSaving ? 'bg-yellow-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
            <Save size={14} /> {isSaving ? 'Speichern...' : 'Gespeichert'}
          </button>
        </div>
      </header>
      
      {/* Tabs */}
      <div className="bg-white border-b px-4 flex">
        {[{ id: 'project', icon: Building, label: 'Projekt' }, { id: 'materials', icon: Box, label: `Materialien (${Object.keys(materials).length})` }, { id: 'types', icon: Layers, label: `Bauteiltypen (${Object.keys(componentTypes).length})` }, { id: 'summary', icon: BarChart3, label: 'Mengen' }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 flex items-center gap-2 border-b-2 ${activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* === PROJECT TAB === */}
        {activeTab === 'project' && (
          <>
            {/* Tree */}
            <div className="w-64 bg-white border-r overflow-y-auto">
              <div className="p-2 border-b flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">STRUKTUR</span>
                <button onClick={addStorey} className="p-1 hover:bg-gray-100 rounded text-blue-600" title="Geschoss hinzufügen"><Plus size={14} /></button>
              </div>
              {project.storeys.map(storey => (
                <div key={storey.id}>
                  <div onClick={() => { setSelectedItem(storey); setSelectedType('storey'); }} className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-gray-50 ${selectedItem?.id === storey.id && selectedType === 'storey' ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}>
                    <button onClick={(e) => { e.stopPropagation(); setExpandedItems({ ...expandedItems, [storey.id]: !expandedItems[storey.id] }); }}>{expandedItems[storey.id] === false ? <ChevronRight size={14} /> : <ChevronDown size={14} />}</button>
                    <Building size={14} className="text-gray-500" />
                    <span className="flex-1 truncate">{storey.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); addSpace(storey.id); }} className="p-0.5 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100"><Plus size={12} /></button>
                  </div>
                  {expandedItems[storey.id] !== false && storey.spaces.map(space => (
                    <div key={space.id}>
                      <div onClick={() => { setSelectedItem(space); setSelectedType('space'); }} className={`flex items-center gap-2 px-2 py-1.5 pl-8 cursor-pointer hover:bg-gray-50 ${selectedItem?.id === space.id && selectedType === 'space' ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}>
                        <button onClick={(e) => { e.stopPropagation(); setExpandedItems({ ...expandedItems, [space.id]: !expandedItems[space.id] }); }}>{expandedItems[space.id] === false ? <ChevronRight size={14} /> : <ChevronDown size={14} />}</button>
                        <Home size={14} className="text-green-600" />
                        <span className="flex-1 truncate">{space.name}</span>
                        <span className="text-xs text-gray-400">{space.components.length}</span>
                      </div>
                      {expandedItems[space.id] !== false && space.components.map(comp => (
                        <div key={comp.id} onClick={() => { setSelectedItem(comp); setSelectedType('component'); }} className={`flex items-center gap-2 px-2 py-1 pl-14 cursor-pointer hover:bg-gray-50 ${selectedItem?.id === comp.id && selectedType === 'component' ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}>
                          <span>{getCatIcon(comp.category)}</span>
                          <span className="flex-1 truncate text-xs">{comp.name}</span>
                          {comp.area > 0 && <span className="text-xs text-gray-400">{formatNumber(comp.area, 1)}m²</span>}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            
            {/* Editor */}
            <div className="flex-1 overflow-y-auto p-4">
              {selectedType === 'storey' && ctx.storey && (
                <div className="bg-white rounded-lg shadow p-4 max-w-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold flex items-center gap-2"><Building size={18} /> Geschoss</h3>
                    <button onClick={() => deleteStorey(ctx.storey.id)} className="text-red-500 p-1 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div><label className="block text-xs text-gray-500 mb-1">Name</label><input value={ctx.storey.name} onChange={(e) => updateStorey(ctx.storey.id, { name: e.target.value })} className="w-full border rounded px-2 py-1.5" /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">Geschosshöhe (m)</label><input type="number" step="0.01" value={ctx.storey.height || ''} onChange={(e) => updateStorey(ctx.storey.id, { height: parseFloat(e.target.value) || 0 })} className="w-full border rounded px-2 py-1.5" /></div>
                  </div>

                  {/* Geschossdecke - das ist die Decke DIESES Geschosses (= Boden des darüber liegenden) */}
                  <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                    <label className="block text-xs text-gray-600 mb-1 font-medium">Geschossdecke</label>
                    <p className="text-xs text-gray-500 mb-2">Deckenaufbau dieses Geschosses (= Boden des Geschosses darüber)</p>
                    <select
                      value={ctx.storey.ceilingTypeRef || ''}
                      onChange={(e) => updateStorey(ctx.storey.id, { ceilingTypeRef: e.target.value || null })}
                      className="w-full border rounded px-2 py-1.5"
                    >
                      <option value="">- Kein Deckentyp -</option>
                      {Object.values(componentTypes).filter(t => t.category === 'ceiling').map(type => (
                        <option key={type.id} value={type.id}>{type.shortCode || type.name}</option>
                      ))}
                    </select>
                    {ctx.storey.spaces && ctx.storey.spaces.length > 0 && (
                      <div className="mt-2 text-xs text-gray-600">
                        Deckenfläche: <strong>{formatNumber(ctx.storey.spaces.reduce((sum, s) => {
                          // Fläche aus pointIds berechnen falls vorhanden
                          if (s.pointIds && s.pointIds.length >= 3 && ctx.storey.points) {
                            const polygon = s.pointIds.map(pid => ctx.storey.points.find(p => p.id === pid)).filter(Boolean);
                            return sum + calcPolygonArea(polygon) / 1000000;
                          }
                          return sum + (s.floorArea || 0);
                        }, 0), 2)} m²</strong>
                      </div>
                    )}
                  </div>

                  {/* Storey Editor Button */}
                  <div className="mb-4">
                    <button
                      onClick={() => openStoreyEditor(ctx.storey.id)}
                      className="w-full flex items-center justify-center gap-2 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg hover:bg-blue-100 hover:border-blue-500 transition-colors"
                    >
                      <PenTool size={24} className="text-blue-600" />
                      <div className="text-left">
                        <span className="font-semibold text-blue-700 block">Grundrisse zeichnen</span>
                        <span className="text-xs text-blue-600">Alle Räume des Geschosses bearbeiten</span>
                      </div>
                    </button>
                    {ctx.storey.spaces.length > 0 && (
                      <div className="mt-2 text-sm text-gray-600 flex items-center gap-4">
                        <span><strong>{ctx.storey.spaces.length}</strong> Räume</span>
                        <span>{ctx.storey.spaces.reduce((sum, s) => sum + (s.components?.length || 0), 0)} Bauteile</span>
                      </div>
                    )}
                  </div>

                  <button onClick={() => addSpace(ctx.storey.id)} className="flex items-center gap-2 text-sm text-blue-600 hover:underline"><Plus size={14} /> Raum manuell hinzufügen</button>
                </div>
              )}
              
              {selectedType === 'space' && ctx.space && (
                <div className="bg-white rounded-lg shadow p-4 max-w-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold flex items-center gap-2"><Home size={18} className="text-green-600" /> Raum</h3>
                    <button onClick={() => deleteSpace(ctx.storey.id, ctx.space.id)} className="text-red-500 p-1 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                  </div>
                  <div className="mb-4"><label className="block text-xs text-gray-500 mb-1">Name</label><input value={ctx.space.name} onChange={(e) => updateSpace(ctx.storey.id, ctx.space.id, { name: e.target.value })} className="w-full border rounded px-2 py-1.5" /></div>

                  {/* Grundriss-Info und Link zum Storey Editor */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    {ctx.space.polygon && ctx.space.polygon.length > 0 ? (
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center justify-between">
                          <span>Grundfläche:</span>
                          <strong>{formatNumber(ctx.space.floorArea || 0, 2)} m²</strong>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Polygon:</span>
                          <span>{ctx.space.polygon.length} Punkte</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Wände:</span>
                          <span>{(ctx.space.walls || []).length} definiert</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Kein Grundriss definiert.</p>
                    )}
                    <button
                      onClick={() => openStoreyEditor(ctx.storey.id)}
                      className="w-full mt-2 flex items-center justify-center gap-2 p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    >
                      <PenTool size={16} />
                      <span className="text-sm">Im Geschoss-Editor bearbeiten</span>
                    </button>
                  </div>

                  <div className="border-t pt-4">
                    <div className="text-xs text-gray-500 mb-2">Manuell Bauteil hinzufügen:</div>
                    <div className="grid grid-cols-4 gap-2">
                      {[{ cat: 'wall', icon: '🧱', label: 'Wand' }, { cat: 'roof', icon: '🏠', label: 'Dach' }, { cat: 'floor', icon: '⬛', label: 'Boden' }, { cat: 'ceiling', icon: '📦', label: 'Decke' }].map(b => (
                        <button key={b.cat} onClick={() => addComponent(ctx.storey.id, ctx.space.id, b.cat)} className="flex flex-col items-center gap-1 p-3 border-2 border-dashed rounded hover:border-blue-400 hover:bg-blue-50">
                          <span className="text-xl">{b.icon}</span><span className="text-xs">+ {b.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {selectedType === 'component' && ctx.component && (
                <div className="bg-white rounded-lg shadow p-4 max-w-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold flex items-center gap-2">{getCatIcon(ctx.component.category)} Bauteil</h3>
                    <button onClick={() => deleteComponent(ctx.storey.id, ctx.space.id, ctx.component.id)} className="text-red-500 p-1 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="block text-xs text-gray-500 mb-1">Bezeichnung</label><input value={ctx.component.name} onChange={(e) => updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { name: e.target.value })} className="w-full border rounded px-2 py-1.5" /></div>
                      <div><label className="block text-xs text-gray-500 mb-1">Kategorie</label><select value={ctx.component.category} onChange={(e) => updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { category: e.target.value })} className="w-full border rounded px-2 py-1.5">
                        <option value="wall">🧱 Wand</option><option value="roof">🏠 Dach</option><option value="floor">⬛ Boden</option><option value="ceiling">📦 Decke</option>
                      </select></div>
                    </div>
                    <div><label className="block text-xs text-gray-500 mb-1">Bauteiltyp</label>
                      <select value={ctx.component.typeRef || ''} onChange={(e) => updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { typeRef: e.target.value || null })} className="w-full border rounded px-2 py-1.5">
                        <option value="">-- Typ wählen --</option>
                        {Object.values(componentTypes).filter(t => t.category === ctx.component.category).map(t => <option key={t.id} value={t.id}>{t.shortCode}: {t.name}</option>)}
                      </select>
                      {ctx.component.typeRef && componentTypes[ctx.component.typeRef] && (
                        <div className="mt-1 p-2 bg-blue-50 rounded text-xs">U={componentTypes[ctx.component.typeRef].uValue} W/(m²K) • {componentTypes[ctx.component.typeRef].totalThickness} mm • {(componentTypes[ctx.component.typeRef].layers || []).length} Schichten</div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><label className="block text-xs text-gray-500 mb-1">Länge (m)</label><input type="number" step="0.01" value={ctx.component.length || ''} onChange={(e) => updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { length: parseFloat(e.target.value) || 0 })} className="w-full border rounded px-2 py-1.5" /></div>
                      <div><label className="block text-xs text-gray-500 mb-1">Höhe (m)</label><input type="number" step="0.01" value={ctx.component.height || ''} onChange={(e) => updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { height: parseFloat(e.target.value) || 0 })} className="w-full border rounded px-2 py-1.5" /></div>
                      <div><label className="block text-xs text-gray-500 mb-1">Brutto (m²)</label><input type="number" value={ctx.component.grossArea || ''} onChange={(e) => updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { grossArea: parseFloat(e.target.value) || 0, area: parseFloat(e.target.value) || 0 })} className="w-full border rounded px-2 py-1.5 bg-gray-50" /></div>
                    </div>
                    {ctx.component.category === 'wall' && (
                      <div>
                        <div className="flex items-center justify-between mb-1"><label className="text-xs text-gray-500">Öffnungen</label>
                          <button onClick={() => { const openings = [...(ctx.component.openings || []), { id: generateId(), name: 'Fenster', width: 1.2, height: 1.5 }]; const openingsArea = openings.reduce((s, o) => s + o.width * o.height, 0); updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { openings, area: (ctx.component.grossArea || 0) - openingsArea }); }} className="text-xs text-blue-600"><Plus size={12} className="inline" /> Hinzufügen</button>
                        </div>
                        {(ctx.component.openings || []).map((op, idx) => (
                          <div key={op.id} className="flex items-center gap-2 mb-1 p-1.5 bg-gray-50 rounded">
                            <input value={op.name} onChange={(e) => { const openings = [...ctx.component.openings]; openings[idx] = { ...op, name: e.target.value }; updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { openings }); }} className="flex-1 border rounded px-2 py-1 text-xs" />
                            <input type="number" step="0.01" value={op.width} onChange={(e) => { const openings = [...ctx.component.openings]; openings[idx] = { ...op, width: parseFloat(e.target.value) || 0 }; const openingsArea = openings.reduce((s, o) => s + o.width * o.height, 0); updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { openings, area: (ctx.component.grossArea || 0) - openingsArea }); }} className="w-14 border rounded px-1 py-1 text-xs" />
                            <span className="text-xs">×</span>
                            <input type="number" step="0.01" value={op.height} onChange={(e) => { const openings = [...ctx.component.openings]; openings[idx] = { ...op, height: parseFloat(e.target.value) || 0 }; const openingsArea = openings.reduce((s, o) => s + o.width * o.height, 0); updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { openings, area: (ctx.component.grossArea || 0) - openingsArea }); }} className="w-14 border rounded px-1 py-1 text-xs" />
                            <span className="text-xs text-gray-500 w-12">={formatNumber(op.width * op.height, 2)}</span>
                            <button onClick={() => { const openings = ctx.component.openings.filter((_, i) => i !== idx); const openingsArea = openings.reduce((s, o) => s + o.width * o.height, 0); updateComponent(ctx.storey.id, ctx.space.id, ctx.component.id, { openings, area: (ctx.component.grossArea || 0) - openingsArea }); }} className="text-red-500"><X size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <span className="text-gray-600">Nettofläche:</span>
                      <span className="text-xl font-bold text-blue-600">{formatNumber(ctx.component.area || ctx.component.grossArea || 0, 2)} m²</span>
                    </div>
                  </div>
                </div>
              )}
              
              {!selectedItem && <div className="text-center text-gray-400 py-16"><Building size={48} className="mx-auto mb-4 opacity-30" /><p>Wähle ein Element aus der Struktur</p></div>}
            </div>
          </>
        )}
        
        {/* === MATERIALS TAB === */}
        {activeTab === 'materials' && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><Search size={16} className="text-gray-400" /><input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Suchen..." className="border rounded px-3 py-1.5 w-64" /></div>
                <button onClick={() => setModalMaterial({})} className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"><Plus size={16} /> Material</button>
              </div>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Kategorie</th><th className="px-3 py-2 text-right">λ</th><th className="px-3 py-2 text-right">μ</th><th className="px-3 py-2 text-right">ρ</th><th className="px-3 py-2 text-right">Preis</th><th className="px-3 py-2"></th></tr></thead>
                  <tbody>
                    {Object.values(materials).filter(m => !searchTerm || m.name.toLowerCase().includes(searchTerm.toLowerCase())).map(m => (
                      <tr key={m.id} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{m.name}</td>
                        <td className="px-3 py-2 text-gray-500">{m.category}</td>
                        <td className="px-3 py-2 text-right font-mono">{m.lambda || '-'}</td>
                        <td className="px-3 py-2 text-right font-mono">{m.mu || '-'}</td>
                        <td className="px-3 py-2 text-right font-mono">{m.density || '-'}</td>
                        <td className="px-3 py-2 text-right text-green-700">{m.price} €/{m.priceUnit}</td>
                        <td className="px-3 py-2 text-right"><button onClick={() => setModalMaterial(m)} className="p-1 hover:bg-gray-100 rounded"><Edit2 size={14} /></button><button onClick={() => handleDeleteMaterial(m.id)} className="p-1 hover:bg-red-50 text-red-500 rounded"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {/* === COMPONENT TYPES TAB === */}
        {activeTab === 'types' && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><Search size={16} className="text-gray-400" /><input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Suchen..." className="border rounded px-3 py-1.5 w-64" /></div>
                <div className="flex gap-2">
                  <button onClick={() => setShowPDFImport(true)} className="flex items-center gap-2 px-3 py-1.5 border rounded hover:bg-gray-50"><FileText size={16} /> PDF Import</button>
                  <button onClick={() => setModalComponentType({})} className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"><Plus size={16} /> Bauteiltyp</button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.values(componentTypes).filter(t => !searchTerm || t.name.toLowerCase().includes(searchTerm.toLowerCase())).map(t => (
                  <div key={t.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{getCatIcon(t.category)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${getCatColor(t.category)}`}>{t.shortCode}</span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setModalComponentType(t)} className="p-1 hover:bg-gray-100 rounded"><Edit2 size={14} /></button>
                        <button onClick={() => handleDeleteComponentType(t.id)} className="p-1 hover:bg-red-50 text-red-500 rounded"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <h3 className="font-medium mb-1">{t.name}</h3>
                    <div className="flex gap-4 text-xs text-gray-500 mb-2">
                      {t.uValue && <span>U={t.uValue} W/(m²K)</span>}
                      <span>{t.totalThickness} mm</span>
                      <span>{(t.layers || []).length} Schichten</span>
                    </div>
                    <div className="text-xs space-y-0.5">
                      {(t.layers || []).slice(0, 3).map((l, i) => <div key={i} className="flex justify-between"><span className="text-gray-600 truncate">{l.name}</span><span>{l.thickness} mm</span></div>)}
                      {(t.layers || []).length > 3 && <div className="text-gray-400">+{(t.layers || []).length - 3} weitere...</div>}
                    </div>
                    {t.source && <div className="mt-2 pt-2 border-t text-xs text-gray-400">Quelle: {t.source}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* === SUMMARY TAB === */}
        {activeTab === 'summary' && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-lg"><div className="text-sm opacity-80">Gesamtfläche</div><div className="text-3xl font-bold">{formatNumber(quantities.totalArea, 1)} m²</div></div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-lg"><div className="text-sm opacity-80">Materialkosten</div><div className="text-3xl font-bold">{formatNumber(quantities.totalCost, 0)} €</div></div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2"><Layers size={16} /> Nach Bauteiltyp</h4>
                {Object.entries(quantities.byType).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-2 border-b last:border-0"><span>{v.name}</span><div><span className="text-gray-500 mr-4">{formatNumber(v.area, 1)} m²</span><span className="font-medium text-green-700">{formatNumber(v.cost, 0)} €</span></div></div>
                ))}
                {Object.keys(quantities.byType).length === 0 && <div className="text-gray-400 text-center py-4">Noch keine Bauteile erfasst</div>}
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2"><Box size={16} /> Nach Material</h4>
                <div className="max-h-64 overflow-y-auto">
                  {Object.entries(quantities.byMaterial).sort((a, b) => b[1].cost - a[1].cost).map(([k, v]) => (
                    <div key={k} className="flex justify-between py-2 border-b last:border-0 text-sm"><div><div>{v.name}</div><div className="text-xs text-gray-400">{v.unit === 'm³' ? `${formatNumber(v.volume, 3)} m³` : `${formatNumber(v.area, 1)} m²`}</div></div><span className="font-medium text-green-700">{formatNumber(v.cost, 0)} €</span></div>
                  ))}
                </div>
                {Object.keys(quantities.byMaterial).length === 0 && <div className="text-gray-400 text-center py-4">Keine Materialien berechnet</div>}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modals */}
      <Modal isOpen={modalMaterial !== null} onClose={() => setModalMaterial(null)} title={modalMaterial?.id ? 'Material bearbeiten' : 'Neues Material'}>
        <MaterialEditor material={modalMaterial} onSave={handleSaveMaterial} onClose={() => setModalMaterial(null)} existingMaterials={materials} />
      </Modal>
      
      <Modal isOpen={modalComponentType !== null} onClose={() => setModalComponentType(null)} title={modalComponentType?.id ? 'Bauteiltyp bearbeiten' : 'Neuer Bauteiltyp'} wide>
        <ComponentTypeEditor componentType={modalComponentType} onSave={handleSaveComponentType} onClose={() => setModalComponentType(null)} materials={materials} />
      </Modal>
      
      <Modal isOpen={showPDFImport} onClose={() => setShowPDFImport(false)} title="PDF Import - Vorschau" wide>
        <PDFImportPreview
          onImport={handlePDFImport}
          onClose={() => setShowPDFImport(false)}
          materials={materials}
          onMaterialsAdded={(newMats) => setMaterials(prev => ({ ...prev, ...newMats }))}
        />
      </Modal>

      <Modal isOpen={showProjectSelector} onClose={() => setShowProjectSelector(false)} title="Projekt öffnen">
        <div className="space-y-4">
          {Object.keys(projectList).length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <FolderOpen size={48} className="mx-auto mb-4 opacity-30" />
              <p>Keine gespeicherten Projekte</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {Object.values(projectList).map(p => (
                <div key={p.id} className={`flex items-center justify-between p-3 border rounded hover:bg-gray-50 ${p.id === project.id ? 'border-blue-500 bg-blue-50' : ''}`}>
                  <div className="flex-1 cursor-pointer" onClick={() => handleOpenProject(p.id)}>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-gray-500">
                      {p.storeys?.length || 0} Geschosse •
                      {p.storeys?.reduce((sum, s) => sum + (s.spaces?.length || 0), 0) || 0} Räume •
                      Erstellt: {p.created ? new Date(p.created).toLocaleDateString('de-DE') : '-'}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleOpenProject(p.id)} className="p-2 text-blue-600 hover:bg-blue-100 rounded" title="Öffnen">
                      <FolderOpen size={16} />
                    </button>
                    {p.id !== project.id && (
                      <button onClick={() => handleDeleteProject(p.id)} className="p-2 text-red-500 hover:bg-red-50 rounded" title="Löschen">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between pt-4 border-t">
            <button onClick={handleNewProject} className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded">
              <FilePlus size={16} /> Neues Projekt
            </button>
            <button onClick={() => setShowProjectSelector(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Schließen</button>
          </div>
        </div>
      </Modal>

      {/* Storey Editor - Full Screen Overlay */}
      {showStoreyEditor && editingStoreyId && (
        <div className="fixed inset-0 z-50 bg-white">
          <StoreyEditor3D
            storey={project.storeys.find(s => s.id === editingStoreyId)}
            onUpdate={handleStoreyUpdate}
            componentTypes={Object.values(componentTypes)}
            onClose={() => {
              setShowStoreyEditor(false);
              setEditingStoreyId(null);
            }}
          />
        </div>
      )}
    </div>
  );
}
